import { useReducer } from 'react';
import { LocationInputMethod } from './input-methods';

interface LocationPickerState {
  method: LocationInputMethod;
  inputValue: string;
  /**
   * The location gotten from an eager input method to use when the user clicks OK.
   */
  cachedLocation?: google.maps.LatLngLiteral;
  dirty: boolean;
}

type ChooseLocationAction = { type: 'CHOOSE_LOCATION' };
type SwitchInputMethodAction = {
  type: 'SWITCH_INPUT_METHOD';
  method: LocationInputMethod;
  eagerLocation?: google.maps.LatLngLiteral;
};
type SetInputValueAction = { type: 'SET_INPUT_VALUE'; value: string };
type LocationPickerAction = ChooseLocationAction | SwitchInputMethodAction | SetInputValueAction;

// eslint-disable-next-line consistent-return
function reducer(state: LocationPickerState, action: LocationPickerAction): LocationPickerState {
  switch (action.type) {
    case 'CHOOSE_LOCATION':
      return { ...state, dirty: false };
    case 'SWITCH_INPUT_METHOD':
      return { ...state, dirty: true, inputValue: '', method: action.method, cachedLocation: action.eagerLocation };
    case 'SET_INPUT_VALUE':
      return { ...state, inputValue: action.value, dirty: true };
  }
}

export function useLocationPickerState(initialInputMethod: LocationInputMethod, startDirty: boolean) {
  const [state, dispatch] = useReducer(reducer, {
    method: initialInputMethod,
    inputValue: '',
    dirty: startDirty
  });
  return {
    method: state.method,
    inputValue: state.inputValue,
    dirty: state.dirty,
    async switchInputMethod(method: LocationInputMethod) {
      const eagerLocation = 'eager' in method && method.eager ? await method.getLocation() : undefined;
      dispatch({ type: 'SWITCH_INPUT_METHOD', method, eagerLocation });
    },
    async setInputValue(value: string) {
      dispatch({ type: 'SET_INPUT_VALUE', value });
    },
    /**
     * Chooses the current inputted location and returns it.
     */
    async chooseLocation() {
      const location = state.cachedLocation ?? (await state.method.getLocation(state.inputValue));
      dispatch({ type: 'CHOOSE_LOCATION' });
      return location;
    }
  };
}
